import { useState } from 'react'
import { Avatar } from '@/components/Avatar'
import { Modal } from '@/components/settings/Modal'
import { ServerProfileForm } from '@/components/guild/ServerProfileForm'
import { avatarUrl, guildIconUrl } from '@/lib/avatar'
import { guildColor, guildInitials } from '@/lib/guildUi'
import { cx } from '@/lib/cx'
import { displayName } from '@/store/social'
import { useAppStore } from '@/store/app'
import { openProfile } from '@/store/contextMenu'
import { usePresenceStore } from '@/store/presence'
import type { Guild, Member, PresenceStatus, Profile, User } from '@/types/api'
import type { Activity } from '@/store/social'
import { GameActivity } from './GameActivity'
import { ProfileCard, type ProfileTab } from './ProfileCard'
import styles from './ProfileModal.module.css'

export type { ProfileTab }

interface Props {
  user: User
  member?: Member | undefined
  guild?: Guild | undefined
  guilds: Record<string, Guild>
  profile?: Profile | undefined
  status: PresenceStatus | undefined
  activity?: Activity | null | undefined
  customStatus?: string | undefined
  activityLine?: string | undefined
  isMe: boolean
  canAddFriend: boolean
  busy: boolean
  error: string | null
  tab: ProfileTab
  onOpenDM: () => void
  onAddFriend: () => void
  onClose: () => void
}

/**
 * Большое окно профиля: слева карточка, справа вкладки. У чужого — «Активность» и общее с ним:
 * друзья и серверы. У своего общего быть не может — вместо него ник и аватар для сервера,
 * если профиль открыт с сервера.
 */
export function ProfileModal(p: Props) {
  const [tab, setTab] = useState<ProfileTab>(p.tab)
  const openGuild = useAppStore((s) => s.openGuild)
  const showGame = usePresenceStore((s) => s.showGame)
  const name = p.member?.nick ?? (p.user.username ? displayName(p.user) : p.user.id)
  const friends = p.profile?.mutual_friends ?? []
  const mutualGuilds = (p.profile?.mutual_guild_ids ?? []).map((id) => p.guilds[id]).filter((g): g is Guild => Boolean(g))
  const tabs: { id: ProfileTab; label: string }[] = p.isMe
    ? [{ id: 'activity', label: 'Активность' }, ...(p.guild ? [{ id: 'server' as const, label: 'Профиль на сервере' }] : [])]
    : [
        { id: 'activity', label: 'Активность' },
        { id: 'friends', label: `${friends.length} · Общие друзья` },
        { id: 'guilds', label: `${mutualGuilds.length} · Общие серверы` },
      ]
  // такой вкладки у этого профиля нет (своему — «общие друзья») — открываем первую
  const active = tabs.some((t) => t.id === tab) ? tab : 'activity'

  return (
    <Modal title={`Профиль ${name}`} onClose={p.onClose} width={840} bare>
      <div className={styles.split}>
        <div className={styles.left}>
          <ProfileCard
            user={p.user}
            member={p.member}
            guild={p.guild}
            guilds={p.guilds}
            profile={p.profile}
            status={p.status}
            activity={p.activity}
            customStatus={p.customStatus}
            activityLine={p.activityLine}
            isMe={p.isMe}
            canAddFriend={p.canAddFriend}
            busy={p.busy}
            error={p.error}
            showSend={false}
            onOpenDM={p.onOpenDM}
            onAddFriend={p.onAddFriend}
            onSend={() => undefined}
          />
        </div>
        <div className={styles.right}>
          <div className={styles.tabs} role="tablist">
            {tabs.map((t) => (
              <button
                key={t.id}
                type="button"
                role="tab"
                aria-selected={t.id === active}
                className={cx(styles.tab, t.id === active && styles.tabActive)}
                onClick={() => setTab(t.id)}
              >
                {t.label}
              </button>
            ))}
          </div>
          <div className={styles.list}>
            {active === 'activity' ? (
              <GameActivity games={p.profile?.games} current={p.activity} isMe={p.isMe} hidden={!showGame} />
            ) : active === 'server' && p.guild ? (
              <div className={styles.pane}>
                <ServerProfileForm guildId={p.guild.id} onLeave={p.onClose} />
              </div>
            ) : active === 'friends' ? (
              friends.length ? friends.map((f) => (
                <button key={f.id} type="button" className={styles.row} onClick={() => openProfile(f.id, { user: f })}>
                  <Avatar initial={displayName(f).slice(0, 1).toUpperCase()} color="#5865F2" size={32} src={avatarUrl(f.id, f.avatar)} decoration={f.avatar_decoration_data} />
                  <span className={styles.rowName}>{displayName(f)}</span>
                </button>
              )) : <p className={styles.empty}>Общих друзей нет</p>
            ) : (
              mutualGuilds.length ? mutualGuilds.map((g) => (
                <button key={g.id} type="button" className={styles.row} onClick={() => { openGuild(g.id); p.onClose() }}>
                  {guildIconUrl(g.id, g.icon)
                    ? <img src={guildIconUrl(g.id, g.icon)!} alt="" className={styles.guildIcon} />
                    : <span className={cx(styles.guildIcon, styles.guildInitials)} style={{ background: guildColor(g.id) }}>{guildInitials(g.name)}</span>}
                  <span className={styles.rowName}>{g.name}</span>
                </button>
              )) : <p className={styles.empty}>Общих серверов нет</p>
            )}
          </div>
        </div>
      </div>
    </Modal>
  )
}
